// MultiCdmSessionList.js
// -----------------------------------------------------------------------------
// The MultiCDM landing screen: every block the customer has ever started, split
// into In Progress and Completed. Sessions are never deleted — an abandoned
// block just stays in the In Progress tab until it's finished.
//
// All data lives in MultiCdmApp; this renders it and reports clicks back.
//
// Props:
//   sessions       array of normalized sessions              (required)
//   loading        bool                                      (required)
//   error          string | null                             (required)
//   onNewBlock     () => void                                (required)
//   onOpenSession  (session) => void  — resume or review     (required)
//   onRenameSession(session, title) => void                  (required)
//   onRetry        () => void                                (optional)
//   onExit         () => void — leave MultiCDM               (optional)
//   renamingId     number | null — save in flight            (optional)
// -----------------------------------------------------------------------------

import React from "react";
import { completedCount, formatElapsed } from "./multiCdmFetches";

function formatDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return (
    MONTHS[date.getMonth()] + " " + date.getDate() + ", " + date.getFullYear()
  );
}

// Total time spent across the finished cases in a block.
function totalElapsed(session) {
  let total = 0;
  let any = false;
  session.CaseProgress.Cases.forEach(function (entry) {
    if (typeof entry.ElapsedSeconds === "number") {
      total += entry.ElapsedSeconds;
      any = true;
    }
  });
  return any ? total : null;
}

class MultiCdmSessionList extends React.Component {
  state = {
    tab: "active", // "active" | "done"
    editingId: null,
    draftTitle: "",
  };

  startRename = (session, e) => {
    e.stopPropagation();
    this.setState({ editingId: session.ID, draftTitle: session.Title });
  };

  commitRename = (session) => {
    const title = this.state.draftTitle.trim();
    this.setState({ editingId: null, draftTitle: "" });
    // An empty box or an unchanged title isn't a rename.
    if (!title || title === session.Title) return;
    this.props.onRenameSession(session, title);
  };

  cancelRename = () => {
    this.setState({ editingId: null, draftTitle: "" });
  };

  handleRenameKey = (session, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      this.commitRename(session);
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.cancelRename();
    }
  };

  renderRow(session) {
    const done = completedCount(session.CaseProgress);
    const total = session.CasesList.length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const created = formatDate(session.CaseProgress.CreatedDate);
    const elapsed = totalElapsed(session);
    const isEditing = this.state.editingId === session.ID;
    const isRenaming = this.props.renamingId === session.ID;

    const meta = [
      total + (total === 1 ? " case" : " cases"),
      done + " of " + total + " complete",
    ];
    if (created) meta.push(created);
    if (session.Completed && elapsed !== null) {
      meta.push(formatElapsed(elapsed) + " total");
    }

    return (
      <div className="mcdm-session" key={session.ID}>
        <div className="mcdm-session__main">
          {isEditing ? (
            <input
              className="mcdm-input"
              type="text"
              autoFocus
              maxLength={120}
              aria-label="Block name"
              value={this.state.draftTitle}
              onChange={(e) => {
                this.setState({ draftTitle: e.target.value });
              }}
              onKeyDown={(e) => {
                this.handleRenameKey(session, e);
              }}
              onBlur={() => {
                this.commitRename(session);
              }}
            />
          ) : (
            <h2 className="mcdm-session__title">
              {session.Title}
              {isRenaming && (
                <span className="mcdm-hint"> &nbsp;Saving...</span>
              )}
            </h2>
          )}
          <p className="mcdm-session__meta">{meta.join(" \u00b7 ")}</p>
          <div
            className="mcdm-progress"
            role="progressbar"
            aria-valuenow={done}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label={done + " of " + total + " cases complete"}
          >
            <div className="mcdm-progress__fill" style={{ width: pct + "%" }} />
          </div>
        </div>
        <div className="mcdm-session__actions">
          {!isEditing && (
            <button
              type="button"
              className="mcdm-btn mcdm-btn--sm"
              onClick={(e) => {
                this.startRename(session, e);
              }}
            >
              Rename
            </button>
          )}
          <button
            type="button"
            className="mcdm-btn mcdm-btn--sm mcdm-btn--primary"
            onClick={() => {
              this.props.onOpenSession(session);
            }}
          >
            {session.Completed ? "Review" : "Resume"}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { sessions, loading, error, onNewBlock, onRetry, onExit } =
      this.props;
    const { tab } = this.state;

    if (loading) {
      return (
        <div className="mcdm-scroll">
          <div className="mcdm-page">
            <div className="mcdm-loading">Loading your blocks...</div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="mcdm-scroll">
          <div className="mcdm-page">
            <div className="mcdm-error">
              <p>{error}</p>
              {onRetry && (
                <button type="button" className="mcdm-btn" onClick={onRetry}>
                  Try again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    const active = sessions.filter(function (session) {
      return !session.Completed;
    });
    const done = sessions.filter(function (session) {
      return session.Completed;
    });
    const shown = tab === "active" ? active : done;

    return (
      <div className="mcdm-scroll">
        <div className="mcdm-page">
          <div className="mcdm-head">
            <h1 className="mcdm-title">
              <small>Multi CDM</small>
              Your blocks
            </h1>
            <div className="mcdm-head-actions">
              {onExit && (
                <button type="button" className="mcdm-btn" onClick={onExit}>
                  Back to CDM cases
                </button>
              )}
              <button
                type="button"
                className="mcdm-btn mcdm-btn--primary"
                onClick={onNewBlock}
              >
                New block
              </button>
            </div>
          </div>

          <div className="mcdm-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "active"}
              className={"mcdm-tab" + (tab === "active" ? " mcdm-tab--on" : "")}
              onClick={() => {
                this.setState({ tab: "active" });
              }}
            >
              In progress
              <span className="mcdm-tab-count">{active.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "done"}
              className={"mcdm-tab" + (tab === "done" ? " mcdm-tab--on" : "")}
              onClick={() => {
                this.setState({ tab: "done" });
              }}
            >
              Completed
              <span className="mcdm-tab-count">{done.length}</span>
            </button>
          </div>

          {shown.length === 0 ? (
            <div className="mcdm-empty">
              {tab === "active" ? (
                <>
                  <h2>No blocks in progress</h2>
                  <p>
                    A block runs several cases back to back, the way the real
                    exam does. Pick up to 13 cases and work through them one
                    after another, with a break screen in between.
                  </p>
                  <button
                    type="button"
                    className="mcdm-btn mcdm-btn--primary"
                    onClick={onNewBlock}
                  >
                    Build your first block
                  </button>
                </>
              ) : (
                <>
                  <h2>Nothing finished yet</h2>
                  <p>
                    Finish every case in a block and it moves here, where you
                    can go through the grades case by case.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="mcdm-sessions">
              {shown.map((session) => {
                return this.renderRow(session);
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default MultiCdmSessionList;